// src/lib/onboardingApplications.ts: shared types, option lists and helpers for
// the allocator, service provider and candidate application flows.
//
// The manager flow keeps its own richer model in ./applications; these three are
// simpler (no regulatory check, no contract) and share one shape.
import { supabase } from '../services/supabase';

export type ApplicantKind = 'manager' | 'allocator' | 'provider' | 'candidate';

/** The table each application type is stored in. */
export const APPLICATION_TABLES: Record<Exclude<ApplicantKind, 'manager'>, string> = {
  allocator: 'allocator_applications',
  provider: 'service_provider_applications',
  candidate: 'candidate_applications',
};

// ── Allocator ────────────────────────────────────────────────────────────────

export const ORGANISATION_TYPES: { value: string; label: string }[] = [
  { value: 'family_office',    label: 'Family Office' },
  { value: 'endowment',        label: 'Endowment' },
  { value: 'foundation',       label: 'Foundation' },
  { value: 'pension_fund',     label: 'Pension Fund' },
  { value: 'sovereign_wealth', label: 'Sovereign Wealth Fund' },
  { value: 'fund_of_funds',    label: 'Fund of Funds' },
  { value: 'insurance',        label: 'Insurance Company' },
  { value: 'bank',             label: 'Private Bank' },
  { value: 'hnwi',             label: 'HNWI / Ultra-HNW' },
  { value: 'other',            label: 'Other' },
];

export const TOTAL_AUM_RANGES: { value: string; label: string }[] = [
  { value: 'under_100m',  label: 'Under $100m' },
  { value: '100m_500m',   label: '$100m - $500m' },
  { value: '500m_1bn',    label: '$500m - $1bn' },
  { value: '1bn_5bn',     label: '$1bn - $5bn' },
  { value: '5bn_10bn',    label: '$5bn - $10bn' },
  { value: 'over_10bn',   label: 'Over $10bn' },
];

export const ALTS_ALLOCATION: { value: string; label: string }[] = [
  { value: 'under_5',  label: 'Under 5%' },
  { value: '5_10',     label: '5-10%' },
  { value: '10_20',    label: '10-20%' },
  { value: '20_30',    label: '20-30%' },
  { value: 'over_30',  label: 'Over 30%' },
];

export const TICKET_SIZES: { value: string; label: string }[] = [
  { value: 'under_1m',  label: 'Under $1m' },
  { value: '1m_5m',     label: '$1m - $5m' },
  { value: '5m_10m',    label: '$5m - $10m' },
  { value: '10m_25m',   label: '$10m - $25m' },
  { value: '25m_50m',   label: '$25m - $50m' },
  { value: 'over_50m',  label: 'Over $50m' },
];

export const QUALIFICATION_BASES: { value: string; label: string }[] = [
  { value: 'mifid_professional', label: 'MiFID II Professional Client' },
  { value: 'institutional',      label: 'Institutional Investor' },
  { value: 'sophisticated',      label: 'Sophisticated Investor' },
  { value: 'family_office',      label: 'Family Office' },
  { value: 'other',              label: 'Other' },
];

export const ASSET_CLASS_OPTIONS: { value: string; label: string }[] = [
  { value: 'hedge_fund',     label: 'Hedge Fund' },
  { value: 'pe',             label: 'Private Equity' },
  { value: 'vc',             label: 'Venture Capital' },
  { value: 're',             label: 'Real Estate' },
  { value: 'credit',         label: 'Credit' },
  { value: 'infrastructure', label: 'Infrastructure' },
];

// ── Service provider ─────────────────────────────────────────────────────────

export const PROVIDER_CATEGORIES = [
  'Legal',
  'Fund Administration',
  'OMS/PMS Technology',
  'Market Data',
  'Insurance',
  'Accounting and Audit',
  'Consulting',
  'Technology and Software',
  'Other',
];

export const PROVIDER_REGIONS = [
  'Global', 'North America', 'Europe', 'Asia Pacific',
  'Middle East and Africa', 'Latin America',
];

export const TARGET_CLIENT_TYPES: { value: string; label: string }[] = [
  { value: 'hedge_fund',    label: 'Hedge Funds' },
  { value: 'pe',            label: 'Private Equity' },
  { value: 'vc',            label: 'Venture Capital' },
  { value: 're',            label: 'Real Estate' },
  { value: 'credit',        label: 'Credit Funds' },
  { value: 'family_office', label: 'Family Offices' },
  { value: 'all',           label: 'All types' },
];

export const PRICING_MODELS: { value: string; label: string }[] = [
  { value: 'subscription', label: 'Annual subscription' },
  { value: 'retainer',     label: 'Monthly retainer' },
  { value: 'project',      label: 'Project-based' },
  { value: 'aum_based',    label: 'AUM-based' },
  { value: 'free',         label: 'Free / freemium' },
];

/** Indicative provider listing price, shown on the apply flow and landing page. */
export const PROVIDER_FROM_PRICE = '£2,000';
export const MANAGER_FROM_PRICE = '£4,150';

// ── Candidate ────────────────────────────────────────────────────────────────

export const CANDIDATE_TARGET_ROLES = [
  'Portfolio Manager', 'Fund Manager', 'Quant Researcher', 'Risk Manager',
  'Business Development', 'Marketing/IR', 'COO', 'CFO', 'Operations Manager',
  'Compliance Officer', 'Data Scientist', 'Technology/Engineering', 'Analyst', 'Other',
];

export const SENIORITY_LEVELS: { value: string; label: string }[] = [
  { value: 'junior', label: 'Junior (0-3 yrs)' },
  { value: 'mid',    label: 'Mid-level (3-7 yrs)' },
  { value: 'senior', label: 'Senior (7+ yrs)' },
];

export const RELOCATION_OPTIONS = [
  'United Kingdom', 'United States', 'Switzerland', 'Singapore',
  'UAE/Dubai', 'Hong Kong', 'Luxembourg', 'Other',
];

export const MAX_CANDIDATE_SKILLS = 10;
export const MAX_CV_BYTES = 5 * 1024 * 1024;

// ── Shared row shapes ────────────────────────────────────────────────────────

interface BaseApplication {
  id: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  heard_about: string | null;
  status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  profile_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AllocatorApplication extends BaseApplication {
  organisation_name: string;
  organisation_type: string;
  organisation_description: string | null;
  organisation_website: string | null;
  headquarters_country: string | null;
  headquarters_city: string | null;
  total_aum_range: string | null;
  alternatives_allocation_pct: string | null;
  asset_classes_of_interest: string[] | null;
  preferred_fund_size: string | null;
  typical_ticket_size: string | null;
  is_professional_investor: boolean;
  qualification_basis: string | null;
  firm_id: string | null;
}

export interface ProviderApplication extends BaseApplication {
  company_name: string;
  category: string;
  company_description: string | null;
  company_website: string | null;
  headquarters_country: string | null;
  headquarters_city: string | null;
  founded_year: number | null;
  client_count: number | null;
  regions_covered: string[] | null;
  primary_services: string | null;
  target_client_types: string[] | null;
  pricing_model: string | null;
  logo_url: string | null;
  demo_video_url: string | null;
  provider_id: string | null;
}

export interface CandidateApplication extends BaseApplication {
  current_role: string | null;
  current_employer: string | null;
  hide_employer: boolean;
  years_experience: number | null;
  seniority: string | null;
  target_roles: string[] | null;
  asset_classes: string[] | null;
  skills: string[] | null;
  relocation_countries: string[] | null;
  open_to_relocation: boolean;
  bio: string | null;
  linkedin_url: string | null;
  cv_url: string | null;
  candidate_id: string | null;
}

export type OnboardingApplication =
  AllocatorApplication | ProviderApplication | CandidateApplication;

/** Statuses that keep an applicant in the holding portal. */
export const ONBOARDING_PENDING_STATUSES = ['submitted', 'admin_review'];

export const ONBOARDING_STATUS_LABELS: Record<string, string> = {
  submitted:    'Submitted',
  admin_review: 'Under Review',
  approved:     'Approved',
  declined:     'Declined',
};

const labelOf = (options: { value: string; label: string }[], v: string | null) =>
  options.find(o => o.value === v)?.label || v || '-';

export const organisationTypeLabel = (v: string | null) => labelOf(ORGANISATION_TYPES, v);
export const aumRangeLabel = (v: string | null) => labelOf(TOTAL_AUM_RANGES, v);
export const allocationLabel = (v: string | null) => labelOf(ALTS_ALLOCATION, v);
export const ticketSizeLabel = (v: string | null) => labelOf(TICKET_SIZES, v);
export const qualificationLabel = (v: string | null) => labelOf(QUALIFICATION_BASES, v);
export const pricingModelLabel = (v: string | null) => labelOf(PRICING_MODELS, v);
export const seniorityLevelLabel = (v: string | null) => labelOf(SENIORITY_LEVELS, v);

export const assetClassLabels = (values: string[] | null) =>
  (values || []).map(v => labelOf(ASSET_CLASS_OPTIONS, v)).join(', ') || '-';

export const clientTypeLabels = (values: string[] | null) =>
  (values || []).map(v => labelOf(TARGET_CLIENT_TYPES, v)).join(', ') || '-';

/**
 * Find whichever onboarding application belongs to the signed-in user.
 * RLS scopes each query to their own rows, so this is safe to run for anyone.
 */
export async function findOwnApplication(): Promise<
  { kind: Exclude<ApplicantKind, 'manager'>; status: string } | null
> {
  const kinds = Object.keys(APPLICATION_TABLES) as Exclude<ApplicantKind, 'manager'>[];
  for (const kind of kinds) {
    const { data } = await supabase.from(APPLICATION_TABLES[kind])
      .select('status').order('created_at', { ascending: false }).limit(1).maybeSingle();
    if (data) return { kind, status: (data as { status: string }).status };
  }
  return null;
}
